import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useAuthContext } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { changePassword } = useAuthContext();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const validationErrors = {};
    
    // Current password validation
    if (!formData.currentPassword) {
      validationErrors.currentPassword = 'Current password is required';
    }
    
    // New password validation
    if (!formData.newPassword) {
      validationErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      validationErrors.newPassword = 'New password must be at least 6 characters';
    }
    
    // Confirm password validation
    if (formData.newPassword !== formData.confirmPassword) {
      validationErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || isSubmitting) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      await changePassword(formData.currentPassword, formData.newPassword);
      toast.success('Password changed successfully');
      onClose();
      // Reset form data
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      if (error.response?.status === 401) {
        setErrors({ ...errors, currentPassword: 'Current password is incorrect' });
      } else {
        toast.error(error.response?.data?.message || 'Failed to change password');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-3xl shadow-xl w-full max-w-md p-6">
          <Dialog.Title className="text-xl font-semibold text-[#6C5DD3] mb-4">
            Change Password
          </Dialog.Title>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="currentPassword">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-white text-black border ${
                  errors.currentPassword ? 'border-red-500' : 'border-gray-200'
                } rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent`}
                placeholder="Enter your current password"
                disabled={isSubmitting}
              />
              {errors.currentPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.currentPassword}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="newPassword">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-white text-black border ${
                  errors.newPassword ? 'border-red-500' : 'border-gray-200'
                } rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent`}
                placeholder="Enter your new password"
                disabled={isSubmitting}
              />
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.newPassword}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirmPassword">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-white text-black border ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                } rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent`}
                placeholder="Confirm your new password"
                disabled={isSubmitting}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
              )}
            </div>
            
            <div className="flex space-x-4 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] text-white font-medium rounded-xl hover:opacity-90 transition duration-200 disabled:opacity-50"
              >
                {isSubmitting ? 'Changing Password...' : 'Change Password'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="py-3 px-4 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] text-white font-medium rounded-xl hover:opacity-90 transition duration-200"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ChangePasswordModal; 